import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MoreHorizontal, Plus, ChevronDown } from 'lucide-react';
const SOURCES = ['Gmail', 'Jira', 'Google Drive', 'Slack'];
const SOURCE_HINTS: Record<string, string> = {
  Gmail: 'Rules sync as Gmail filters. Changes apply in your inbox.',
  Jira: 'Rules create JQL-based automation. Labels are applied to matching issues.',
  'Google Drive': 'Rules apply labels to matching documents.',
  Slack: 'Rules watch channels and threads for matching keywords.'
};
const INITIAL_RULES: Record<string, Array<{
  id: number;
  text: string;
  status: 'active' | 'paused';
}>> = {
  Gmail: [{
    id: 1,
    text: 'From **billing@** or **invoice@** \u2192 label **Finance**, archive',
    status: 'active'
  }, {
    id: 2,
    text: 'Subject contains **urgent** or **asap** \u2192 label **Priority**',
    status: 'active'
  }, {
    id: 3,
    text: 'From **@acme.com** \u2192 label **Clients**',
    status: 'active'
  }, {
    id: 4,
    text: 'From **notifications@github.com** \u2192 label **Dev**, archive',
    status: 'paused'
  }],
  Jira: [{
    id: 5,
    text: 'Project is **ZEN** and type is **Bug** \u2192 label **Triage**',
    status: 'active'
  }, {
    id: 6,
    text: 'Priority is **Highest** \u2192 alert **On-call**',
    status: 'active'
  }],
  'Google Drive': [{
    id: 7,
    text: 'Folder is **Invoices** \u2192 label **Finance**',
    status: 'active'
  }],
  Slack: [{
    id: 8,
    text: 'Channel is **#support** and contains **urgent** \u2192 alert **On-call**',
    status: 'active'
  }]
};
export function SourceRules() {
  const [activeTab, setActiveTab] = useState('Gmail');
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const activeRules = INITIAL_RULES[activeTab] || [];
  const renderRuleText = (text: string) => {
    const parts = text.split(/\*\*(.*?)\*\*/g);
    return parts.map((part, i) => i % 2 === 1 ? <span key={i} className="font-medium text-[#e8e4df]" data-id="element-1904">{part}</span> : <span key={i} data-id="element-1905">{part}</span>);
  };
  return <div className="min-h-[calc(100vh-56px)] text-[#e8e4df] selection:bg-[#4a9a9a]/30 pt-12 pb-32 px-6" data-id="element-1906">
      <div className="max-w-[640px] mx-auto w-full flex flex-col" data-id="element-1907">
        <header className="mb-8" data-id="element-1908">
          <h1 className="text-[20px] font-bold text-[#e8e4df] tracking-tight mb-1" data-id="element-1909">Source Rules</h1>
          <p className="text-[#8a8680] text-[14px]" data-id="element-1910">Classify data where it lives. Less noise, better signal.</p>
        </header>

        <nav className="flex items-center gap-6 mb-6 border-b border-[#8a8680]/10" data-id="element-1911">
          {SOURCES.map(source => {
          const isActive = activeTab === source;
          return <button key={source} onClick={() => {
            setActiveTab(source);
            setIsBuilderOpen(false);
          }} className={`pb-3 text-[14px] font-medium transition-colors relative ${isActive ? 'text-[#e8e4df]' : 'text-[#8a8680] hover:text-[#e8e4df]/80'}`} data-id="element-1912">
                {source}
                {isActive && <motion.div layoutId="activeTabIndicator" className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#4a9a9a]" initial={false} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} data-id="element-1913" />}
              </button>;
        })}
        </nav>

        <div className="bg-[#1c1c22] rounded-[16px] shadow-xl shadow-black/20 overflow-hidden mb-4" data-id="element-1914">
          {activeRules.length > 0 ? activeRules.map((rule, index) => <div key={rule.id} className={`flex items-center justify-between p-6 transition-colors hover:bg-[#252530]/50 ${index !== activeRules.length - 1 ? 'border-b border-[#8a8680]/10' : ''}`} data-id="element-1915">
              <div className="text-[14px] text-[#8a8680] leading-[1.6] pr-6" data-id="element-1916">{renderRuleText(rule.text)}</div>
              <div className="flex items-center gap-4 shrink-0" data-id="element-1917">
                <div className={`w-2 h-2 rounded-full ${rule.status === 'active' ? 'bg-[#4a9a9a]' : 'bg-amber-600/80'}`} title={rule.status === 'active' ? 'Active' : 'Paused'} data-id="element-1918" />
                <button className="text-[#8a8680]/40 hover:text-[#8a8680] transition-colors" data-id="element-1919"><MoreHorizontal size={18} data-id="element-1920" /></button>
              </div>
            </div>) : <div className="p-6 text-[14px] text-[#8a8680] text-center" data-id="element-1921">No rules configured for {activeTab}.</div>}
        </div>

        <p className="text-[13px] text-[#8a8680] mb-6 px-2" data-id="element-1922">{SOURCE_HINTS[activeTab]}</p>

        <AnimatePresence initial={false} data-id="element-1923">
          {isBuilderOpen && <motion.div initial={{
          opacity: 0,
          height: 0,
          y: -10
        }} animate={{
          opacity: 1,
          height: 'auto',
          y: 0
        }} exit={{
          opacity: 0,
          height: 0,
          y: -10
        }} transition={{
          duration: 0.3,
          ease: [0.25, 0.1, 0.25, 1]
        }} className="overflow-hidden mb-6" data-id="element-1924">
              <div className="bg-[#1c1c22] rounded-[16px] shadow-xl shadow-black/20 p-6" data-id="element-1925">
                <div className="space-y-5" data-id="element-1926">
                  <div className="flex flex-wrap items-center gap-2.5 text-[14px]" data-id="element-1927">
                    <span className="text-[#8a8680] font-medium" data-id="element-1928">When</span>
                    <select className="bg-[#252530] text-[#e8e4df] rounded-md pl-3 pr-8 py-1.5 outline-none appearance-none cursor-pointer border border-transparent focus:border-[#4a9a9a]/50" data-id="element-1929"><option data-id="element-1930">From</option><option data-id="element-1931">Subject</option><option data-id="element-1932">Contains</option><option data-id="element-1933">Has attachment</option></select>
                    <span className="text-[#8a8680]" data-id="element-1934">matches</span>
                    <input type="text" placeholder="billing@" className="bg-[#252530] text-[#e8e4df] rounded-md px-3 py-1.5 outline-none border border-transparent focus:border-[#4a9a9a]/50 placeholder:text-[#8a8680]/40 w-40" data-id="element-1935" />
                    <button className="text-[#8a8680] hover:text-[#e8e4df] flex items-center gap-1 px-2 py-1 rounded hover:bg-[#252530]" data-id="element-1936"><Plus size={14} data-id="element-1937" /> Add condition</button>
                  </div>
                  <div className="flex flex-wrap items-center gap-2.5 text-[14px]" data-id="element-1938">
                    <span className="text-[#8a8680] font-medium" data-id="element-1939">Then</span>
                    <select className="bg-[#252530] text-[#e8e4df] rounded-md pl-3 pr-8 py-1.5 outline-none appearance-none cursor-pointer border border-transparent focus:border-[#4a9a9a]/50" data-id="element-1940"><option data-id="element-1941">Label as</option><option data-id="element-1942">Archive</option><option data-id="element-1943">Star</option><option data-id="element-1944">Mark important</option><option data-id="element-1945">Skip inbox</option></select>
                    <input type="text" placeholder="Finance" className="bg-[#252530] text-[#e8e4df] rounded-md px-3 py-1.5 outline-none border border-transparent focus:border-[#4a9a9a]/50 placeholder:text-[#8a8680]/40 w-32" data-id="element-1946" />
                    <button className="text-[#8a8680] hover:text-[#e8e4df] flex items-center gap-1 px-2 py-1 rounded hover:bg-[#252530]" data-id="element-1947"><Plus size={14} data-id="element-1948" /> Add action</button>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-3 mt-8 pt-6 border-t border-[#8a8680]/10" data-id="element-1949">
                  <button onClick={() => setIsBuilderOpen(false)} className="text-[#8a8680] hover:text-[#e8e4df] px-4 py-2 rounded-md text-[14px] font-medium" data-id="element-1950">Cancel</button>
                  <button onClick={() => setIsBuilderOpen(false)} className="bg-[#4a9a9a] text-[#141418] font-medium px-5 py-2 rounded-md hover:bg-[#5bbaba] text-[14px] shadow-lg shadow-[#4a9a9a]/20" data-id="element-1951">Create Rule</button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>

        {!isBuilderOpen && <button onClick={() => setIsBuilderOpen(true)} className="flex items-center gap-2 text-[14px] font-medium text-[#8a8680] hover:text-[#e8e4df] transition-colors px-2 py-1 rounded-md hover:bg-[#1c1c22]" data-id="element-1952">
            <Plus size={16} data-id="element-1953" /> Add rule
          </button>}
      </div>
    </div>;
}